import {ReactElement} from 'react';
import {Navigate, Outlet} from 'react-router-dom';
import {auth, useAuth} from '../use-auth';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {useSettings} from '@ui/settings/use-settings';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {replace} from '@remix-run/router/utils';

interface Props {
  children?: ReactElement;
  permission?: string;
  role?: string[];
  requireLogin?: boolean;
  redirectUrl?: string;
}
export function AuthRoute({children, permission, role, requireLogin = true, redirectUrl}: Props) {
  const {user, isLoggedIn, hasPermission, isSubscribed} = useAuth();
  const isAdmin : boolean = hasPermission('admin') || (['admin']).every(
    a => user?.roles.find(b => b.name === a),
  );
  const {billing} = useSettings();
  if (
    (requireLogin && !isLoggedIn) ||
    (permission && !hasPermission(permission)) ||
    (role && !isAdmin && !(role).some( a => user?.roles.find(b => b.name === a) ))
  ) {
    if (isLoggedIn) {
      return redirectUrl ? (
        <Navigate to={redirectUrl} replace />
      ) : (
        billing.enable && !isSubscribed ? (
          <Navigate to="/pricing" replace />
        ) : (
          <NotFoundPage />
        )
      );
    }
    return redirectUrl ? (
      <Navigate to={redirectUrl} replace />
    ) : (
      <Navigate to="/login" replace />
    );
  }
  return children || <Outlet />;
}

interface AuthRouteLoaderProps {
  permission?: string;
  role?: string[];
  requireLogin?: boolean;
  redirectUrl?: string;
}
export function authGuard({
  permission,
  role,
  requireLogin = true,
  redirectUrl,
}: AuthRouteLoaderProps = {}) {
  const billingEnabled = getBootstrapData().settings.billing.enable;
  const isAdmin : boolean = auth.hasPermission('admin') || (['admin']).every(
    a => auth.user?.roles.find(b => b.name === a),
  );

  if (
    (requireLogin && !auth.isLoggedIn) ||
    (permission && !auth.hasPermission(permission)) ||
    (role && !isAdmin && !(role).some( a => auth.user?.roles.find(b => b.name === a) ))
  ) {
    if (auth.isLoggedIn) {
      return redirectUrl ? replace(redirectUrl) : (
        billingEnabled && !auth.isSubscribed
          ? replace('/pricing')
          : replace('/404')
      );
    }
    return redirectUrl ? replace(redirectUrl) : replace('/login');
  }

  return null;
}
